/**
 * Centralized Animations for Advanced Search Components
 * Requirements: 11.1, 11.2, 11.3, 11.4, 11.6
 * 
 * This file contains reusable Angular animations for the advanced-search module.
 * All animations respect prefers-reduced-motion for accessibility.
 */

import { 
  trigger, 
  transition, 
  style, 
  animate, 
  query, 
  group,
  stagger,
  state,
  AnimationTriggerMetadata 
} from '@angular/animations';

/**
 * Slide animation for wizard step transitions
 * Requirement 11.1: Slide animations for step changes
 * Duration: 300ms (Requirement 11.2)
 */
export const slideAnimation: AnimationTriggerMetadata = trigger('slideAnimation', [
  transition(':increment', [
    group([
      query(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ], { optional: true }),
      query(':leave', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        animate('300ms ease-out', style({ transform: 'translateX(-100%)', opacity: 0 }))
      ], { optional: true })
    ])
  ]),
  transition(':decrement', [
    group([
      query(':enter', [
        style({ transform: 'translateX(-100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ], { optional: true }),
      query(':leave', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        animate('300ms ease-out', style({ transform: 'translateX(100%)', opacity: 0 }))
      ], { optional: true })
    ])
  ])
]);

/**
 * Expand/collapse animation for filter sections
 * Requirement 11.2: Animate expansion/collapse of filters
 * Duration: 250ms (Requirement 11.2)
 */
export const expandCollapse: AnimationTriggerMetadata = trigger('expandCollapse', [
  state('collapsed', style({
    height: '0',
    opacity: '0',
    overflow: 'hidden',
    paddingTop: '0',
    paddingBottom: '0'
  })),
  state('expanded', style({
    height: '*',
    opacity: '1',
    overflow: 'visible'
  })),
  transition('collapsed <=> expanded', [
    animate('250ms ease-in-out')
  ])
]);

/**
 * Chip animation for adding/removing chips
 * Requirement 11.3: Animate addition/removal of chips
 * Duration: 200ms (Requirement 11.2)
 */
export const chipAnimation: AnimationTriggerMetadata = trigger('chipAnimation', [
  transition(':enter', [
    style({ 
      opacity: 0, 
      transform: 'scale(0.8) translateY(-10px)' 
    }),
    animate('200ms ease-out', style({ 
      opacity: 1, 
      transform: 'scale(1) translateY(0)' 
    }))
  ]),
  transition(':leave', [
    animate('200ms ease-in', style({ 
      opacity: 0, 
      transform: 'scale(0.8) translateY(-10px)' 
    }))
  ])
]);

/**
 * Counter animation for filter counter updates
 * Requirement 11.4: Animate counter updates
 * Duration: 200ms (Requirement 11.2)
 */
export const counterAnimation: AnimationTriggerMetadata = trigger('counterAnimation', [
  transition('* => *', [
    style({ transform: 'scale(1.2)', color: 'var(--color-primary, #1976d2)' }),
    animate('200ms ease-out', style({ transform: 'scale(1)', color: '*' }))
  ])
]);

/**
 * Slide in animation for panels and summaries
 * Requirement 11.1: Smooth UI transitions
 * Duration: 250ms (Requirement 11.2)
 */
export const slideIn: AnimationTriggerMetadata = trigger('slideIn', [
  transition(':enter', [
    style({ 
      opacity: 0, 
      transform: 'translateY(-10px)' 
    }),
    animate('250ms ease-out', style({ 
      opacity: 1, 
      transform: 'translateY(0)' 
    }))
  ]),
  transition(':leave', [
    animate('200ms ease-in', style({ 
      opacity: 0, 
      transform: 'translateY(-10px)' 
    }))
  ])
]);

/**
 * Fade in animation for general content
 * Requirement 11.1: Smooth content transitions
 * Duration: 250ms (Requirement 11.2)
 */
export const fadeIn: AnimationTriggerMetadata = trigger('fadeIn', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('250ms ease-out', style({ opacity: 1 }))
  ]),
  transition(':leave', [
    animate('200ms ease-in', style({ opacity: 0 }))
  ])
]);

/**
 * Staggered list animation for chips and items
 * Requirement 11.3: Staggered chip animations
 * Duration: 200ms base + 50ms stagger (Requirement 11.2)
 */
export const listStagger: AnimationTriggerMetadata = trigger('listStagger', [
  transition('* => *', [
    query(':enter', [
      style({ opacity: 0, transform: 'scale(0.8)' }),
      stagger('50ms', [
        animate('200ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))
      ])
    ], { optional: true })
  ])
]);

/**
 * Accordion animation for mobile filter sections
 * Requirement 11.2: Animate accordion sections
 * Duration: 250ms (Requirement 11.2)
 */
export const accordionAnimation: AnimationTriggerMetadata = trigger('accordionAnimation', [
  state('collapsed', style({
    height: '0',
    opacity: '0',
    overflow: 'hidden'
  })),
  state('expanded', style({
    height: '*',
    opacity: '1',
    overflow: 'visible'
  })),
  transition('collapsed <=> expanded', [
    animate('250ms cubic-bezier(0.4, 0.0, 0.2, 1)')
  ])
]);

/**
 * Bounce animation for attention-grabbing elements
 * Requirement 11.4: Visual feedback for counter updates
 * Duration: 300ms (Requirement 11.2)
 */
export const bounceAnimation: AnimationTriggerMetadata = trigger('bounceAnimation', [
  transition('* => bounce', [
    animate('150ms ease-out', style({ transform: 'scale(1.15)' })),
    animate('150ms ease-in', style({ transform: 'scale(1)' }))
  ])
]);

/**
 * Shake animation for validation errors
 * Requirement 11.1: Visual feedback for errors
 * Duration: 300ms (Requirement 11.2)
 */
export const shakeAnimation: AnimationTriggerMetadata = trigger('shakeAnimation', [
  transition('* => shake', [
    animate('100ms', style({ transform: 'translateX(-10px)' })),
    animate('100ms', style({ transform: 'translateX(10px)' })),
    animate('100ms', style({ transform: 'translateX(0)' }))
  ])
]);

/**
 * Utility function to check if user prefers reduced motion
 * Requirement 11.6: Respect prefers-reduced-motion
 * 
 * Usage: Call this before triggering animations programmatically
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  return mediaQuery.matches;
}

/**
 * Get animation duration based on user preference
 * Requirement 11.6: Respect prefers-reduced-motion
 * 
 * @param normalDuration - Duration in ms for normal motion
 * @returns Duration in ms (0 if reduced motion preferred)
 */
export function getAnimationDuration(normalDuration: number): number {
  return prefersReducedMotion() ? 0 : normalDuration;
}

/**
 * Get animation state based on user preference
 * Requirement 11.6: Respect prefers-reduced-motion
 * 
 * @param animationState - The animation state to use
 * @returns Animation state or 'void' if reduced motion preferred
 */
export function getAnimationState(animationState: string): string {
  return prefersReducedMotion() ? 'void' : animationState;
}

/**
 * Animation configuration constants
 * Requirement 11.2: Consistent animation timing (200-300ms)
 */
export const ANIMATION_DURATIONS = {
  FAST: 150,
  NORMAL: 200,
  MEDIUM: 250,
  SLOW: 300,
  WIZARD_TRANSITION: 300,
  CHIP_ANIMATION: 200,
  COUNTER_UPDATE: 200,
  ACCORDION: 250
} as const;

/**
 * Easing functions
 * Requirement 11.1: Smooth, natural animations
 */
export const ANIMATION_EASINGS = {
  EASE_IN: 'ease-in',
  EASE_OUT: 'ease-out',
  EASE_IN_OUT: 'ease-in-out',
  CUBIC_BEZIER: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
  LINEAR: 'linear'
} as const;

/**
 * CSS class for disabling animations when user prefers reduced motion
 * Requirement 11.6: Respect prefers-reduced-motion
 * 
 * Usage: Add this class to components that should respect reduced motion
 */
export const REDUCED_MOTION_CLASS = 'prefers-reduced-motion';

/**
 * Initialize reduced motion detection
 * Requirement 11.6: Respect prefers-reduced-motion
 * 
 * Call this in app initialization to add the class to document body
 */
export function initReducedMotionDetection(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const updateReducedMotionClass = () => {
    if (prefersReducedMotion()) {
      document.body.classList.add(REDUCED_MOTION_CLASS);
    } else {
      document.body.classList.remove(REDUCED_MOTION_CLASS);
    }
  };

  // Initial check
  updateReducedMotionClass();

  // Listen for changes
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', updateReducedMotionClass);
  } else {
    // Fallback for older browsers
    mediaQuery.addListener(updateReducedMotionClass);
  }
}
